import { Autocomplete, Box, Chip, TextField } from '@mui/material'
import { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TruncatedText from './TruncatedText'
import { CancelRounded } from '@mui/icons-material'

interface StringArrayInputProps {
    id: string
    label: string
    value: string[]
    onChange: (value: string[]) => void
    disabled?: boolean
    error?: boolean
    helperText?: string
    allowedCharacters?: RegExp
}

const StringArrayInput = ({
    id,
    label,
    value,
    onChange,
    disabled = false,
    error: externalError,
    helperText,
    allowedCharacters,
}: StringArrayInputProps): ReactElement => {
    const { t } = useTranslation()
    const [inputValue, setInputValue] = useState('')
    const [error, setError] = useState<string | null>(null)

    const handleChange = useCallback(
        (event: React.SyntheticEvent, newValue: string[]) => {
            if (new Set(newValue).size !== newValue.length) {
                setError(t('valueAlreadyExists'))
                return
            }
            onChange(newValue)
            setInputValue('')
            setError(null)
        },
        [onChange, t],
    )

    const handleBlur = useCallback(() => {
        if (inputValue?.trim()) {
            const newItem = inputValue.trim()
            if (value?.includes(newItem)) {
                setError(t('valueAlreadyExists'))
            } else {
                onChange([...(value || []), newItem])
                setInputValue('')
                setError(null)
            }
        } else {
            setError(null)
        }
    }, [inputValue, onChange, value, t])

    return (
        <Box sx={{ width: '100%' }}>
            <Autocomplete
                multiple
                id={id}
                options={[]}
                freeSolo
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && inputValue?.trim()) {
                        const newItem = inputValue.trim()
                        if (value?.includes(newItem)) {
                            event.preventDefault()
                            setError(t('valueAlreadyExists'))
                        }
                    }
                }}
                value={value || []}
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'reset') {
                        return
                    }
                    if (
                        allowedCharacters &&
                        !allowedCharacters.test(newInputValue)
                    ) {
                        return
                    }
                    setInputValue(newInputValue)
                    setError(null)
                }}
                clearOnBlur={false}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={disabled}
                renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                        <Chip
                            label={
                                <TruncatedText
                                    text={option}
                                    sx={{ fontSize: '14px' }}
                                />
                            }
                            {...getTagProps({ index })}
                            sx={{ m: '2px', maxWidth: 'calc(100% - 5px)' }}
                            key={index}
                            deleteIcon={
                                <CancelRounded id={`delete-icon-${option}`} />
                            }
                            size="small"
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder={
                            value?.length > 0 ? '' : t('typeAndPressEnter')
                        }
                        label={label}
                        error={!!error || !!externalError}
                        helperText={error || helperText}
                    />
                )}
            />
        </Box>
    )
}

export default StringArrayInput
